// Firestore-ready pricing data for VendorScout
// Collected via Playwright web scraping - January 2026
// Schema follows SOFTWARE_DETAIL_GUIDE.md

export const pricingData = {
  // ============================================
  // RMM / Remote Monitoring
  // ============================================

  atera: {
    id: "atera",
    name: "Atera",
    website: "atera.com",
    category: "rmm",
    description: "All-in-one RMM, PSA, and remote access platform with AI-powered IT management. Unique per-technician pricing model.",

    pricingModel: "per-technician",

    billingCycles: [
      { name: "Monthly" },
      { name: "Annual", discount: 17 }
    ],

    tiers: [
      {
        name: "Professional",
        price: 149,
        unit: "technician",
        billing: "per month (annual)",
        description: "For small IT teams",
        features: [
          "Unlimited endpoints",
          "Remote monitoring & management",
          "Patch management",
          "Remote access",
          "Ticketing & PSA",
          "Reporting",
          { name: "Network discovery", included: false },
          { name: "AI Copilot", included: false }
        ]
      },
      {
        name: "Expert",
        price: 189,
        unit: "technician",
        billing: "per month (annual)",
                description: "For growing MSPs",
        features: [
          "Everything in Professional",
          "Network discovery",
          "SNMP monitoring",
          "Asset management",
          "Custom reports"
        ]
      },
      {
        name: "Master",
        price: 219,
        unit: "technician",
        billing: "per month (annual)",
        description: "For larger operations",
        features: [
          "Everything in Expert",
          "AI Copilot",
          "Advanced automation",
          "Priority support"
        ]
      },
      {
        name: "Enterprise",
        price: null,
        enterprise: true,
        description: "For large organizations",
        features: [
          "Everything in Master",
          "Dedicated account manager",
          "Custom integrations",
          "SLA guarantees",
          "Advanced security features"
        ]
      }
    ],

    notes: [
      "Unique per-technician pricing - unlimited endpoints included",
      "Monthly pricing approximately 17% higher than annual",
      "30-day free trial available",
      "No minimum endpoint requirements"
    ],

    enterpriseContact: true,
    lastUpdated: "2026-01-08"
  },

  ninjarmm: {
    id: "ninjarmm",
    name: "NinjaOne (NinjaRMM)",
    website: "ninjaone.com",
    category: "rmm",
    description: "Unified IT management platform combining RMM, patch management, and endpoint management with modern UX.",

    pricingModel: "per-endpoint",

    tiers: [
      {
        name: "NinjaOne RMM",
        price: null,
        description: "Core RMM platform with comprehensive features",
        features: [
          "Remote monitoring & management",
          "Patch management (Windows, Mac, Linux)",
          "Remote access (built-in)",
          "Scripting & automation",
          "Ticketing",
          "Reporting & analytics",
          "Mobile app"
        ]
      }
    ],

    addOns: [
      {
        name: "NinjaOne Backup",
        description: "Integrated cloud and local backup solution",
        price: null
      },
      {
        name: "NinjaOne MDM",
        description: "Mobile device management",
        price: null
      },
      {
        name: "NinjaOne Endpoint Security",
        description: "Built-in endpoint protection",
        price: null
      }
    ],

    notes: [
      "Per-endpoint pricing - contact sales for quote",
      "Pricing varies based on endpoint count and add-ons selected",
      "14-day free trial available",
      "Recently rebranded from NinjaRMM to NinjaOne"
    ],

    enterpriseContact: true,
    lastUpdated: "2026-01-08"
  },

  "connectwise-automate": {
    id: "connectwise-automate",
    name: "ConnectWise Automate",
    website: "connectwise.com",
    category: "rmm",
    description: "Enterprise-grade RMM platform with powerful automation capabilities for MSPs.",

    pricingModel: "custom",

    notes: [
      "Contact sales for pricing - varies by endpoint count and features",
      "Typically bundled with ConnectWise suite (Manage, Control, etc.)",
      "Enterprise-focused with complex pricing structures",
      "Volume discounts available for MSPs"
    ],

    enterpriseContact: true,
    lastUpdated: "2026-01-08"
  },

  "datto-rmm": {
    id: "datto-rmm",
    name: "Datto RMM",
    website: "datto.com",
    category: "rmm",
    description: "Cloud-native RMM platform designed for MSPs with integrated security features.",

    pricingModel: "custom",

    notes: [
      "MSP partner pricing model - contact for quote",
      "Part of Kaseya/Datto ecosystem",
      "Bundled pricing options with other Datto products",
      "Volume discounts available"
    ],

    enterpriseContact: true,
    lastUpdated: "2026-01-08"
  },

  "solarwinds-rmm": {
    id: "solarwinds-rmm",
    name: "SolarWinds RMM",
    website: "solarwinds.com",
    category: "rmm",
    description: "Comprehensive RMM solution with integrated backup, antivirus, and security features.",

    pricingModel: "per-endpoint",

    notes: [
      "Per-endpoint pricing - contact sales for quote",
      "Now part of N-able (spun off from SolarWinds)",
      "Tiered pricing based on features and endpoint count",
      "MSP-focused with partner program discounts"
    ],

    enterpriseContact: true,
    lastUpdated: "2026-01-08"
  },

  // ============================================
  // Antivirus / EDR
  // ============================================

  crowdstrike: {
    id: "crowdstrike",
    name: "CrowdStrike Falcon",
    website: "crowdstrike.com",
    category: "antivirus",
    description: "Cloud-native endpoint protection platform with AI-powered threat detection and response.",

    pricingModel: "per-endpoint",

    tiers: [
      {
        name: "Falcon Go",
        price: 59.99,
        unit: "device",
        billing: "per year",
        description: "Essential protection for small businesses",
        features: [
          "Next-gen antivirus",
          "Ransomware protection",
          "USB device control",
          "5-25 devices"
        ],
        limits: {
          "Devices": "5-25"
        }
      },
      {
        name: "Falcon Pro",
        price: 99.99,
        unit: "device",
        billing: "per year",
                description: "Advanced protection with firewall management",
        features: [
          "Everything in Falcon Go",
          "Firewall management",
          "Enhanced visibility",
          "5-25 devices"
        ],
        limits: {
          "Devices": "5-25"
        }
      },
      {
        name: "Falcon Enterprise",
        price: 184.99,
        unit: "device",
        billing: "per year",
        description: "Full EDR capabilities",
        features: [
          "Everything in Falcon Pro",
          "Endpoint detection & response (EDR)",
          "Threat intelligence",
          "Threat hunting",
          "5-25 devices"
        ],
        limits: {
          "Devices": "5-25"
        }
      },
      {
        name: "Falcon Elite",
        price: null,
        enterprise: true,
        description: "Maximum protection with identity threat detection",
        features: [
          "Everything in Falcon Enterprise",
          "Identity threat detection",
          "IT hygiene",
          "Integrated threat intelligence"
        ]
      },
      {
        name: "Falcon Complete MDR",
        price: null,
        enterprise: true,
        description: "Managed detection and response service",
        features: [
          "24/7 managed threat hunting",
          "Expert-driven response",
          "Full remediation",
          "Dedicated security team"
        ]
      }
    ],

    volumeDiscounts: {
      notes: "Volume discounts available for 26+ endpoints. Contact sales for enterprise pricing."
    },

    notes: [
      "Prices shown for 5-25 device tier",
      "Enterprise and MDR tiers require sales consultation",
      "Monthly billing not available for small bundles",
      "15-day free trial available"
    ],

    enterpriseContact: true,
    lastUpdated: "2026-01-08"
  },

  sentinelone: {
    id: "sentinelone",
    name: "SentinelOne",
    website: "sentinelone.com",
    category: "antivirus",
    description: "AI-powered endpoint protection, detection, and response platform.",

    pricingModel: "per-endpoint",

    tiers: [
      {
        name: "Singularity Core",
        price: null,
        description: "Foundational protection",
        features: [
          "Next-gen antivirus",
          "Behavioral AI detection",
          "Automated remediation",
          "1-click rollback"
        ]
      },
      {
        name: "Singularity Control",
        price: null,
                description: "Enhanced control and visibility",
        features: [
          "Everything in Core",
          "Firewall control",
          "Device control",
          "Network quarantine",
          "Application inventory"
        ]
      },
      {
        name: "Singularity Complete",
        price: null,
        description: "Full EDR capabilities",
        features: [
          "Everything in Control",
          "ActiveEDR",
          "Deep Visibility",
          "Threat hunting",
          "MITRE ATT&CK mapping"
        ]
      },
      {
        name: "Singularity Commercial",
        price: null,
        enterprise: true,
        description: "Enterprise-grade protection",
        features: [
          "Everything in Complete",
          "Identity security",
          "Cloud workload protection",
          "Mobile threat defense"
        ]
      }
    ],

    pricingSummary: {
      avgPerSeat: 45,
      perSeatRange: [30, 65],
      quoteCount: 8,
      commonDiscount: 20
    },

    notes: [
      "Contact sales for specific pricing",
      "Pricing varies significantly by volume and contract length",
      "MSP/MSSP partner programs available",
      "Community-reported pricing ranges from $30-65/endpoint/year"
    ],

    enterpriseContact: true,
    lastUpdated: "2026-01-08"
  },

  sophos: {
    id: "sophos",
    name: "Sophos",
    website: "sophos.com",
    category: "antivirus",
    description: "Comprehensive cybersecurity solutions including endpoint, network, and cloud security.",

    pricingModel: "per-endpoint",

    tiers: [
      {
        name: "Intercept X Essentials",
        price: null,
        description: "Core endpoint protection",
        features: [
          "Deep learning malware detection",
          "Anti-ransomware",
          "Exploit prevention",
          "Malicious traffic detection"
        ]
      },
      {
        name: "Intercept X Advanced",
        price: null,
                description: "Advanced threat protection",
        features: [
          "Everything in Essentials",
          "Root cause analysis",
          "Sophos Central management",
          "Live Response"
        ]
      },
      {
        name: "Intercept X Advanced with XDR",
        price: null,
        description: "Extended detection and response",
        features: [
          "Everything in Advanced",
          "Cross-product data integration",
          "AI-guided investigations",
          "Automated threat response"
        ]
      },
      {
        name: "Managed Detection and Response",
        price: null,
        enterprise: true,
        description: "24/7 managed security service",
        features: [
          "24/7 threat hunting",
          "Expert-led response",
          "Full-scale incident response",
          "Weekly/monthly reporting"
        ]
      }
    ],

    notes: [
      "Free trials available for all tiers",
      "Contact sales or partner for pricing",
      "MSP partner program with volume discounts",
      "Home user pricing differs significantly from business"
    ],

    enterpriseContact: true,
    lastUpdated: "2026-01-08"
  },

  bitdefender: {
    id: "bitdefender",
    name: "Bitdefender GravityZone",
    website: "bitdefender.com",
    category: "antivirus",
    description: "Enterprise endpoint security platform with advanced threat protection, EDR capabilities, and centralized management.",

    pricingModel: "per-member",

    billingCycles: [
      { name: "Monthly" },
      { name: "Annual" }
    ],

    tiers: [
      {
        name: "Ultimate Small Business Security - 3 Members",
        price: 189.99,
        unit: "year",
        billing: "or $18.99/month",
        description: "For very small teams",
        features: [
          "6 devices included",
          "1 server included",
          "Antivirus & antimalware",
          "Ransomware protection",
          "Password manager",
          "VPN included"
        ],
        limits: {
          "Members": "3",
          "Devices": "6",
          "Servers": "1"
        }
      },
      {
        name: "Ultimate Small Business Security - 5 Members",
        price: 269.99,
        unit: "year",
        description: "For small teams",
        features: [
          "10 devices included",
          "2 servers included",
          "Antivirus & antimalware",
          "Ransomware protection",
          "Password manager",
          "VPN included"
        ],
        limits: {
          "Members": "5",
          "Devices": "10",
          "Servers": "2"
        }
      },
      {
        name: "Ultimate Small Business Security - 10 Members",
        price: 399.99,
        unit: "year",
                description: "For growing teams",
        features: [
          "20 devices included",
          "3 servers included",
          "Antivirus & antimalware",
          "Ransomware protection",
          "Password manager",
          "VPN included"
        ],
        limits: {
          "Members": "10",
          "Devices": "20",
          "Servers": "3"
        }
      },
      {
        name: "Ultimate Small Business Security - 25 Members",
        price: 799.99,
        unit: "year",
        billing: "or $79.99/month",
        description: "For larger small businesses",
        features: [
          "50 devices included",
          "5 servers included",
          "Antivirus & antimalware",
          "Ransomware protection",
          "Password manager",
          "VPN included"
        ],
        limits: {
          "Members": "25",
          "Devices": "50",
          "Servers": "5"
        }
      },
      {
        name: "GravityZone Business Security Enterprise",
        price: null,
        enterprise: true,
        description: "Full enterprise EDR/XDR solution",
        features: [
          "EDR (Endpoint Detection & Response)",
          "XDR capabilities",
          "Risk analytics",
          "Sandbox Analyzer",
          "HyperDetect",
          "Managed Detection & Response available"
        ]
      }
    ],

    addOns: [
      {
        name: "Email Security",
        description: "Cloud-based email protection",
        price: null
      },
      {
        name: "Full Disk Encryption",
        description: "Managed encryption for endpoints",
        price: null
      },
      {
        name: "Patch Management",
        description: "Automated patch deployment",
        price: null
      },
      {
        name: "Security for Mobile",
        description: "Mobile device protection",
        price: null
      }
    ],

    notes: [
      "Small business pricing shown - enterprise pricing varies",
      "GravityZone enterprise tiers require sales consultation",
      "MSP partner pricing available with different structure",
      "30-day free trial available"
    ],

    enterpriseContact: true,
    lastUpdated: "2026-01-08"
  },

  webroot: {
    id: "webroot",
    name: "Webroot Business Endpoint Protection",
    website: "webroot.com",
    category: "antivirus",
    description: "Cloud-based endpoint protection with fast deployment and minimal system impact. Now part of OpenText Cybersecurity.",

    pricingModel: "per-device",

    billingCycles: [
      { name: "Annual" }
    ],

    tiers: [
      {
        name: "Business Endpoint Protection",
        price: 30,
        unit: "device",
        billing: "per year",
        description: "Core endpoint protection",
        features: [
          "Cloud-based protection",
          "Real-time threat intelligence",
          "Fast scanning",
          "Minimal system impact",
          "Central management console",
          "Identity protection"
        ],
        limits: {
          "Min Devices": "5"
        }
      }
    ],

    addOns: [
      {
        name: "Security Awareness Training",
        description: "Employee phishing and security training",
        price: null
      },
      {
        name: "DNS Protection",
        description: "Network-level threat blocking",
        price: null
      },
      {
        name: "EDR",
        description: "Endpoint detection and response capabilities",
        price: null
      },
      {
        name: "MDR",
        description: "Managed detection and response service",
        price: null
      }
    ],

    notes: [
      "Base pricing is $150/year for 5 devices ($30/device)",
      "Now part of OpenText Cybersecurity portfolio",
      "Volume discounts available for larger deployments",
      "MSP partner pricing available"
    ],

    lastUpdated: "2026-01-08"
  },

  // ============================================
  // Backup & Recovery
  // ============================================

  veeam: {
    id: "veeam",
    name: "Veeam",
    website: "veeam.com",
    category: "backup",
    description: "Enterprise backup, recovery, and data management platform for virtual, physical, and cloud workloads.",

    pricingModel: "custom",

    tiers: [
      {
        name: "Foundation (Secure Foundation)",
        price: null,
        description: "Core backup capabilities",
        features: [
          "Backup & recovery",
          "Instant VM recovery",
          "Backup copy jobs",
          "Basic reporting"
        ]
      },
      {
        name: "Advanced (Cyber Resilience)",
        price: null,
                description: "Enhanced data protection",
        features: [
          "Everything in Foundation",
          "Immutability features",
          "Advanced monitoring",
          "SureBackup verification",
          "Ransomware protection"
        ]
      },
      {
        name: "Premium (Enterprise Resilience)",
        price: null,
        enterprise: true,
        description: "Maximum enterprise protection",
        features: [
          "Everything in Advanced",
          "Veeam ONE monitoring",
          "Enterprise Manager",
          "Advanced orchestration",
          "Priority support"
        ]
      }
    ],

    notes: [
      "Universal License model - contact sales for pricing",
      "Pricing based on workloads protected",
      "VUL (Veeam Universal License) provides flexibility",
      "MSP/VCSP partner programs available",
      "Free Community Edition available for small environments"
    ],

    enterpriseContact: true,
    lastUpdated: "2026-01-08"
  },

  acronis: {
    id: "acronis",
    name: "Acronis Cyber Protect",
    website: "acronis.com",
    category: "backup",
    description: "Unified backup, disaster recovery, and cybersecurity platform combining data protection with anti-malware.",

    pricingModel: "per-workstation",

    billingCycles: [
      { name: "Annual" },
      { name: "3-Year", discount: 30 },
      { name: "5-Year", discount: 40 }
    ],

    tiers: [
      {
        name: "Cyber Protect Standard",
        price: 85,
        unit: "workstation",
        billing: "per year",
        description: "Essential backup and protection",
        features: [
          "Full-image backup",
          "File-level backup",
          "Disk cloning",
          "Active protection (anti-ransomware)",
          "Vulnerability assessments"
        ]
      },
      {
        name: "Cyber Protect Backup Advanced",
        price: 109,
        unit: "workstation",
        billing: "per year",
        description: "Enhanced backup capabilities",
        features: [
          "Everything in Standard",
          "Microsoft 365 backup",
          "Advanced backup features",
          "Continuous data protection"
        ]
      },
      {
        name: "Cyber Protect Advanced",
        price: 129,
        unit: "workstation",
        billing: "per year",
                description: "Full backup and security suite",
        features: [
          "Everything in Backup Advanced",
          "Advanced anti-malware",
          "URL filtering",
          "Exploit prevention",
          "Patch management"
        ]
      }
    ],

    addOns: [
      {
        name: "Disaster Recovery",
        description: "Cloud-based disaster recovery",
        price: null
      },
      {
        name: "Advanced Security + EDR",
        description: "Enhanced security with endpoint detection",
        price: null
      },
      {
        name: "Advanced Email Security",
        description: "Email threat protection",
        price: null
      }
    ],

    notes: [
      "Prices shown for workstations - server pricing higher",
      "Multi-year discounts: 3-year saves ~30%, 5-year saves ~40%",
      "MSP/service provider pricing model available",
      "Cloud storage priced separately"
    ],

    lastUpdated: "2026-01-08"
  },

  "datto-bcdr": {
    id: "datto-bcdr",
    name: "Datto BCDR",
    website: "datto.com",
    category: "backup",
    description: "Business continuity and disaster recovery solution designed specifically for MSPs.",

    pricingModel: "custom",

    notes: [
      "MSP partner pricing model - contact for quote",
      "Pricing based on appliance model and cloud storage",
      "Requires Datto partnership",
      "Includes hardware appliance + cloud backup",
      "Per-device agent licensing"
    ],

    enterpriseContact: true,
    lastUpdated: "2026-01-08"
  },

  backblaze: {
    id: "backblaze",
    name: "Backblaze",
    website: "backblaze.com",
    category: "backup",
    description: "Simple, affordable cloud backup for businesses and personal use.",

    pricingModel: "per-computer",

    billingCycles: [
      { name: "Monthly" },
      { name: "Annual", discount: 17 },
      { name: "2-Year", discount: 25 }
    ],

    tiers: [
      {
        name: "Backblaze Computer Backup",
        price: 9,
        unit: "computer",
        billing: "per month",
        description: "Unlimited cloud backup for computers",
        features: [
          "Unlimited backup storage",
          "Automatic continuous backup",
          "File versioning (1 year)",
          "Web restore",
          "USB drive restore option",
          "Mobile app access"
        ]
      },
      {
        name: "Backblaze B2 Cloud Storage",
        price: 0.006,
        unit: "GB",
        billing: "per month",
        description: "S3-compatible object storage",
        features: [
          "First 10GB free",
          "S3-compatible API",
          "Lifecycle rules",
          "Object lock immutability"
        ]
      },
      {
        name: "Business Backup (Teams)",
        price: 9,
        unit: "computer",
        billing: "per month",
        description: "Backup with admin controls",
        features: [
          "Everything in Personal Backup",
          "Central admin console",
          "Group management",
          "SSO integration",
          "Minimum 3 licenses"
        ],
        limits: {
          "Min Licenses": "3"
        }
      }
    ],

    notes: [
      "Annual pricing: $99/computer/year (save ~17%)",
      "2-year pricing: $189/computer (save ~25%)",
      "B2 storage: $6/TB/month, free egress to partners",
      "Business Groups minimum 3 licenses"
    ],

    lastUpdated: "2026-01-08"
  },

  // ============================================
  // CRM
  // ============================================

  salesforce: {
    id: "salesforce",
    name: "Salesforce",
    website: "salesforce.com",
    category: "crm",
    description: "The world's leading CRM platform with comprehensive sales, service, and marketing automation.",

    pricingModel: "per-user",

    billingCycles: [
      { name: "Annual" }
    ],

    tiers: [
      {
        name: "Starter Suite",
        price: 25,
        unit: "user",
        billing: "per month (billed annually)",
        description: "Simple CRM for small teams",
        features: [
          "Account & contact management",
          "Lead management",
          "Opportunity tracking",
          "Email integration",
          "Mobile app"
        ]
      },
      {
        name: "Pro Suite",
        price: 100,
        unit: "user",
        billing: "per month (billed annually)",
        description: "Complete CRM for any size team",
        features: [
          "Everything in Starter",
          "Forecast management",
          "Customizable dashboards",
          "Quote management",
          "Custom apps & integrations"
        ]
      },
      {
        name: "Enterprise",
        price: 165,
        unit: "user",
        billing: "per month (billed annually)",
                description: "Deeply customizable CRM",
        features: [
          "Everything in Pro Suite",
          "Advanced customization",
          "Workflow automation",
          "Territory management",
          "Approval processes"
        ]
      },
      {
        name: "Unlimited",
        price: 330,
        unit: "user",
        billing: "per month (billed annually)",
        description: "Unlimited CRM power and support",
        features: [
          "Everything in Enterprise",
          "24/7 support",
          "Premier Success resources",
          "Sandbox environments",
          "Unlimited custom apps"
        ]
      },
      {
        name: "Einstein 1 Sales",
        price: 500,
        unit: "user",
        billing: "per month (billed annually)",
        enterprise: true,
        description: "Best of Salesforce with AI",
        features: [
          "Everything in Unlimited",
          "Einstein AI",
          "Sales engagement",
          "Revenue intelligence",
          "Slack integration"
        ]
      }
    ],

    addOns: [
      {
        name: "CPQ (Configure, Price, Quote)",
        description: "Complex quote management",
        price: 75,
        unit: "user/month"
      },
      {
        name: "Sales Cloud Einstein",
        description: "AI-powered sales insights",
        price: 50,
        unit: "user/month"
      }
    ],

    notes: [
      "Annual billing required",
      "Prices are USD, may vary by region",
      "Add-ons available for marketing, service, commerce",
      "Non-profit and education discounts available"
    ],

    enterpriseContact: true,
    lastUpdated: "2026-01-08"
  },

  hubspot: {
    id: "hubspot",
    name: "HubSpot",
    website: "hubspot.com",
    category: "crm",
    description: "Integrated CRM platform with marketing, sales, service, and content management tools.",

    pricingModel: "hybrid",

    billingCycles: [
      { name: "Monthly" },
      { name: "Annual", discount: 10 }
    ],

    tiers: [
      {
        name: "Free Tools",
        price: 0,
        description: "Basic CRM features forever free",
        features: [
          "Contact management",
          "Deal pipeline",
          "Meeting scheduling",
          "Live chat",
          "Forms",
          "Email marketing (limited)"
        ],
        limits: {
          "Contacts": "Up to 1M",
          "Users": "Unlimited"
        }
      },
      {
        name: "Sales Hub Starter",
        price: 20,
        unit: "seat",
        billing: "per month",
        description: "Essential sales tools",
        features: [
          "Everything in Free",
          "Email tracking",
          "Document sharing",
          "Calling (500 mins)",
          "Meeting scheduling",
          "1:1 email"
        ]
      },
      {
        name: "Sales Hub Professional",
        price: 100,
        unit: "seat",
        billing: "per month",
                description: "Advanced sales automation",
        features: [
          "Everything in Starter",
          "Sequences",
          "Forecasting",
          "Custom reporting",
          "Sales automation",
          "eSignature"
        ]
      },
      {
        name: "Sales Hub Enterprise",
        price: 150,
        unit: "seat",
        billing: "per month",
        enterprise: true,
        description: "Enterprise-grade sales CRM",
        features: [
          "Everything in Professional",
          "Custom objects",
          "Predictive lead scoring",
          "Recurring revenue tracking",
          "Advanced permissions",
          "Sandboxes"
        ],
        limits: {
          "Min Seats": "10"
        }
      }
    ],

    notes: [
      "Free CRM with generous features available",
      "Platform fee may apply for Starter ($20/mo)",
      "Annual discount of ~10%",
      "Bundled CRM Suite available at discount",
      "Non-profit discounts available"
    ],

    lastUpdated: "2026-01-08"
  },

  "zoho-crm": {
    id: "zoho-crm",
    name: "Zoho CRM",
    website: "zoho.com",
    category: "crm",
    description: "Affordable CRM with comprehensive features and deep integration with Zoho's suite of business apps.",

    pricingModel: "per-user",

    billingCycles: [
      { name: "Monthly" },
      { name: "Annual", discount: 34 }
    ],

    tiers: [
      {
        name: "Free",
        price: 0,
        description: "For home offices",
        features: [
          "3 users max",
          "Lead management",
          "Contact management",
          "Deal tracking",
          "Tasks & events"
        ],
        limits: {
          "Users": "3"
        }
      },
      {
        name: "Standard",
        price: 14,
        unit: "user",
        billing: "per month (annual)",
        description: "Basic sales automation",
        features: [
          "Scoring rules",
          "Workflows",
          "Multiple pipelines",
          "Mass email",
          "Custom dashboards"
        ]
      },
      {
        name: "Professional",
        price: 23,
        unit: "user",
        billing: "per month (annual)",
                description: "Complete CRM for any team",
        features: [
          "Everything in Standard",
          "SalesSignals",
          "Blueprint process",
          "Inventory management",
          "Web-to-case forms"
        ]
      },
      {
        name: "Enterprise",
        price: 40,
        unit: "user",
        billing: "per month (annual)",
        description: "Advanced customization",
        features: [
          "Everything in Professional",
          "Zia AI assistant",
          "Canvas design studio",
          "Multi-user portals",
          "Advanced customization"
        ]
      },
      {
        name: "Ultimate",
        price: 52,
        unit: "user",
        billing: "per month (annual)",
        enterprise: true,
        description: "Maximum features and limits",
        features: [
          "Everything in Enterprise",
          "Advanced BI",
          "Enhanced storage",
          "Dedicated database cluster"
        ]
      }
    ],

    notes: [
      "Monthly pricing approximately 34% higher than annual",
      "Free tier available for up to 3 users",
      "Zoho One bundle includes CRM + 40+ apps",
      "Excellent value compared to competitors"
    ],

    lastUpdated: "2026-01-08"
  },

  pipedrive: {
    id: "pipedrive",
    name: "Pipedrive",
    website: "pipedrive.com",
    category: "crm",
    description: "Sales-focused CRM with intuitive visual pipeline management and powerful automation.",

    pricingModel: "per-user",

    billingCycles: [
      { name: "Monthly" },
      { name: "Annual", discount: 17 }
    ],

    tiers: [
      {
        name: "Essential",
        price: 14,
        unit: "user",
        billing: "per month (annual)",
        description: "Simple sales tracking",
        features: [
          "Lead and deal management",
          "Customizable pipelines",
          "Email inbox",
          "Activity reminders",
          "Data import/export"
        ]
      },
      {
        name: "Advanced",
        price: 34,
        unit: "user",
        billing: "per month (annual)",
                description: "Email sync and automation",
        features: [
          "Everything in Essential",
          "Full email sync",
          "Email templates",
          "Workflow automation",
          "Meeting scheduler"
        ]
      },
      {
        name: "Professional",
        price: 49,
        unit: "user",
        billing: "per month (annual)",
        description: "Advanced reporting and forecasting",
        features: [
          "Everything in Advanced",
          "Revenue forecasting",
          "Custom fields formulas",
          "Document management",
          "eSignatures"
        ]
      },
      {
        name: "Power",
        price: 64,
        unit: "user",
        billing: "per month (annual)",
        description: "For larger teams",
        features: [
          "Everything in Professional",
          "Project management",
          "Phone support",
          "Free implementation"
        ]
      },
      {
        name: "Enterprise",
        price: 99,
        unit: "user",
        billing: "per month (annual)",
        enterprise: true,
        description: "Unlimited features and security",
        features: [
          "Everything in Power",
          "Unlimited everything",
          "Security dashboard",
          "Custom security rules",
          "SSO (SAML)"
        ]
      }
    ],

    addOns: [
      {
        name: "LeadBooster",
        description: "Lead generation tools",
        price: 32.50,
        unit: "company/month"
      },
      {
        name: "Web Visitors",
        description: "Website visitor tracking",
        price: 41,
        unit: "company/month"
      },
      {
        name: "Campaigns",
        description: "Email marketing",
        price: 13,
        unit: "company/month"
      },
      {
        name: "Smart Docs",
        description: "Document automation",
        price: 32.50,
        unit: "company/month"
      }
    ],

    notes: [
      "14-day free trial available",
      "Monthly pricing approximately 17% higher",
      "Add-ons available separately or bundled"
    ],

    lastUpdated: "2026-01-08"
  },

  freshsales: {
    id: "freshsales",
    name: "Freshsales",
    website: "freshworks.com",
    category: "crm",
    description: "AI-powered CRM from Freshworks with built-in phone, email, and chat capabilities.",

    pricingModel: "per-user",

    billingCycles: [
      { name: "Monthly" },
      { name: "Annual", discount: 20 }
    ],

    tiers: [
      {
        name: "Free",
        price: 0,
        description: "Free forever for up to 3 users",
        features: [
          "Contact management",
          "Deal management",
          "Built-in phone & email",
          "Mobile app",
          "Chat widget"
        ],
        limits: {
          "Users": "3"
        }
      },
      {
        name: "Growth",
        price: 9,
        unit: "user",
        billing: "per month (annual)",
        description: "For growing teams",
        features: [
          "Everything in Free",
          "Visual sales pipeline",
          "AI contact scoring",
          "1000 marketing emails/month",
          "Built-in chat, email & phone"
        ]
      },
      {
        name: "Pro",
        price: 39,
        unit: "user",
        billing: "per month (annual)",
                description: "For larger sales teams",
        features: [
          "Everything in Growth",
          "Multiple sales pipelines",
          "Time-based workflows",
          "AI insights",
          "3000 marketing emails/month"
        ]
      },
      {
        name: "Enterprise",
        price: 59,
        unit: "user",
        billing: "per month (annual)",
        enterprise: true,
        description: "For enterprises",
        features: [
          "Everything in Pro",
          "Custom modules",
          "AI forecasting",
          "Audit logs",
          "10000 marketing emails/month",
          "Dedicated account manager"
        ]
      }
    ],

    notes: [
      "Free tier available for up to 3 users",
      "Monthly pricing approximately 20% higher",
      "Freshsales Suite bundles sales + marketing",
      "21-day free trial on paid plans"
    ],

    lastUpdated: "2026-01-08"
  },

  // ============================================
  // Project Management
  // ============================================

  jira: {
    id: "jira",
    name: "Jira",
    website: "atlassian.com",
    category: "project",
    description: "Industry-leading project management and issue tracking tool, especially popular for software development teams.",

    pricingModel: "per-user",

    billingCycles: [
      { name: "Monthly" },
      { name: "Annual" }
    ],

    tiers: [
      {
        name: "Free",
        price: 0,
        description: "For small teams",
        features: [
          "Up to 10 users",
          "Scrum & Kanban boards",
          "Backlog management",
          "Reports & dashboards",
          "2 GB storage"
        ],
        limits: {
          "Users": "10",
          "Storage": "2 GB"
        }
      },
      {
        name: "Standard",
        price: 8.15,
        unit: "user",
        billing: "per month",
        description: "For growing teams",
        features: [
          "Everything in Free",
          "Up to 35,000 users",
          "Project roles",
          "250 GB storage",
          "Audit logs (180 days)"
        ]
      },
      {
        name: "Premium",
        price: 16,
        unit: "user",
        billing: "per month",
                description: "For scaling organizations",
        features: [
          "Everything in Standard",
          "Advanced roadmaps",
          "Sandbox environment",
          "Unlimited storage",
          "24/7 premium support",
          "99.9% uptime SLA"
        ]
      },
      {
        name: "Enterprise",
        price: null,
        enterprise: true,
        description: "For large enterprises",
        features: [
          "Everything in Premium",
          "Unlimited sites",
          "Atlassian Analytics",
          "BYOK encryption",
          "Advanced security controls"
        ]
      }
    ],

    volumeDiscounts: {
      notes: "Pricing decreases as user count increases. Contact sales for 1000+ users."
    },

    notes: [
      "Cloud pricing shown - Data Center pricing differs",
      "Free tier for up to 10 users",
      "Volume discounts for larger teams",
      "Part of Atlassian ecosystem (Confluence, Bitbucket)"
    ],

    lastUpdated: "2026-01-08"
  },

  asana: {
    id: "asana",
    name: "Asana",
    website: "asana.com",
    category: "project",
    description: "Work management platform for teams to coordinate, manage, and track their work.",

    pricingModel: "per-user",

    billingCycles: [
      { name: "Monthly" },
      { name: "Annual", discount: 20 }
    ],

    tiers: [
      {
        name: "Personal",
        price: 0,
        description: "For individuals and small teams",
        features: [
          "Unlimited tasks & projects",
          "List, Board, Calendar views",
          "Assignee and due dates",
          "iOS and Android apps",
          "Up to 10 users"
        ],
        limits: {
          "Users": "10"
        }
      },
      {
        name: "Starter",
        price: 10.99,
        unit: "user",
        billing: "per month (annual)",
        description: "For growing teams",
        features: [
          "Everything in Personal",
          "Timeline view",
          "Forms",
          "Task dependencies",
          "Unlimited free guests"
        ]
      },
      {
        name: "Advanced",
        price: 24.99,
        unit: "user",
        billing: "per month (annual)",
                description: "For teams that need more",
        features: [
          "Everything in Starter",
          "Portfolios",
          "Goals",
          "Workload management",
          "Custom rules builder",
          "Approvals"
        ]
      },
      {
        name: "Enterprise",
        price: null,
        enterprise: true,
        description: "For large organizations",
        features: [
          "Everything in Advanced",
          "Admin controls",
          "SAML SSO",
          "Custom branding",
          "Data export API",
          "Priority support"
        ]
      },
      {
        name: "Enterprise+",
        price: null,
        enterprise: true,
        description: "For complex organizations",
        features: [
          "Everything in Enterprise",
          "AI features",
          "Advanced integrations",
          "Custom implementation"
        ]
      }
    ],

    notes: [
      "Free tier for up to 10 team members",
      "Monthly pricing approximately 20% higher",
      "Non-profit discounts available"
    ],

    lastUpdated: "2026-01-08"
  },

  monday: {
    id: "monday",
    name: "Monday.com",
    website: "monday.com",
    category: "project",
    description: "Flexible work operating system for teams to manage projects, workflows, and collaboration.",

    pricingModel: "per-user",

    billingCycles: [
      { name: "Monthly" },
      { name: "Annual", discount: 18 }
    ],

    tiers: [
      {
        name: "Free",
        price: 0,
        description: "For individuals",
        features: [
          "Up to 2 seats",
          "3 boards",
          "Unlimited docs",
          "200+ templates",
          "iOS and Android apps"
        ],
        limits: {
          "Seats": "2",
          "Boards": "3"
        }
      },
      {
        name: "Basic",
        price: 9,
        unit: "seat",
        billing: "per month (annual)",
        description: "Manage team tasks & workflows",
        features: [
          "Unlimited items",
          "Unlimited boards",
          "5 GB storage",
          "Prioritized customer support"
        ],
        limits: {
          "Min Seats": "3"
        }
      },
      {
        name: "Standard",
        price: 12,
        unit: "seat",
        billing: "per month (annual)",
                description: "Collaborate & optimize workflows",
        features: [
          "Everything in Basic",
          "Timeline & Gantt views",
          "Calendar view",
          "Automations (250/month)",
          "Integrations (250/month)",
          "20 GB storage"
        ]
      },
      {
        name: "Pro",
        price: 19,
        unit: "seat",
        billing: "per month (annual)",
        description: "Streamline complex workflows",
        features: [
          "Everything in Standard",
          "Private boards & docs",
          "Chart view",
          "Time tracking",
          "Formula column",
          "Automations (25,000/month)",
          "100 GB storage"
        ]
      },
      {
        name: "Enterprise",
        price: null,
        enterprise: true,
        description: "Enterprise-grade security & control",
        features: [
          "Everything in Pro",
          "Enterprise-scale automations",
          "Multi-level permissions",
          "Advanced reporting",
          "Tailored onboarding",
          "Premium support"
        ]
      }
    ],

    notes: [
      "Minimum 3 seats for paid plans",
      "Monthly pricing approximately 18% higher",
      "Work OS approach - highly customizable",
      "Free tier limited to 2 seats"
    ],

    lastUpdated: "2026-01-08"
  },

  clickup: {
    id: "clickup",
    name: "ClickUp",
    website: "clickup.com",
    category: "project",
    description: "All-in-one productivity platform combining project management, docs, goals, and chat.",

    pricingModel: "per-user",

    billingCycles: [
      { name: "Monthly" },
      { name: "Annual", discount: 20 }
    ],

    tiers: [
      {
        name: "Free Forever",
        price: 0,
        description: "For personal use",
        features: [
          "Unlimited tasks",
          "Unlimited members",
          "100 MB storage",
          "Two-Factor Authentication",
          "24/7 support"
        ],
        limits: {
          "Storage": "100 MB"
        }
      },
      {
        name: "Unlimited",
        price: 7,
        unit: "member",
        billing: "per month (annual)",
        description: "For small teams",
        features: [
          "Everything in Free",
          "Unlimited storage",
          "Unlimited integrations",
          "Unlimited dashboards",
          "Guests with permissions",
          "Goals & portfolios"
        ]
      },
      {
        name: "Business",
        price: 12,
        unit: "member",
        billing: "per month (annual)",
                description: "For mid-sized teams",
        features: [
          "Everything in Unlimited",
          "Google SSO",
          "Advanced automations",
          "Time tracking",
          "Workload management",
          "Timelines & mind maps"
        ]
      },
      {
        name: "Enterprise",
        price: null,
        enterprise: true,
        description: "For large teams",
        features: [
          "Everything in Business",
          "White labeling",
          "Advanced permissions",
          "Enterprise API",
          "SAML SSO",
          "Custom role creation"
        ]
      }
    ],

    notes: [
      "Free tier with unlimited members (limited features)",
      "Monthly pricing approximately 20% higher",
      "All-in-one approach aims to replace multiple tools",
      "Regular feature updates and additions"
    ],

    lastUpdated: "2026-01-08"
  },

  trello: {
    id: "trello",
    name: "Trello",
    website: "trello.com",
    category: "project",
    description: "Visual collaboration tool using boards, lists, and cards for project organization.",

    pricingModel: "per-user",

    billingCycles: [
      { name: "Monthly" },
      { name: "Annual", discount: 17 }
    ],

    tiers: [
      {
        name: "Free",
        price: 0,
        description: "For individuals or small teams",
        features: [
          "Unlimited cards",
          "Up to 10 boards/workspace",
          "Unlimited Power-Ups",
          "250 automation runs/month",
          "iOS and Android apps"
        ],
        limits: {
          "Boards": "10"
        }
      },
      {
        name: "Standard",
        price: 5,
        unit: "user",
        billing: "per month (annual)",
        description: "For small teams",
        features: [
          "Everything in Free",
          "Unlimited boards",
          "Advanced checklists",
          "Custom fields",
          "1000 automation runs/month"
        ]
      },
      {
        name: "Premium",
        price: 10,
        unit: "user",
        billing: "per month (annual)",
                description: "For teams that need more views",
        features: [
          "Everything in Standard",
          "Dashboard, Timeline, Calendar views",
          "Workspace views",
          "Admin controls",
          "Unlimited automation runs",
          "Priority support"
        ]
      },
      {
        name: "Enterprise",
        price: 17.50,
        unit: "user",
        billing: "per month (annual)",
        enterprise: true,
        description: "For organizations",
        features: [
          "Everything in Premium",
          "Multi-board guests",
          "Attachment permissions",
          "Organization-wide permissions",
          "Public board management"
        ],
        limits: {
          "Min Users": "50"
        }
      }
    ],

    notes: [
      "Free tier with generous features",
      "Part of Atlassian - integrates with Jira, Confluence",
      "Enterprise requires minimum 50 users",
      "Simple, visual approach to project management"
    ],

    lastUpdated: "2026-01-08"
  },

  // ============================================
  // HR / Payroll
  // ============================================

  gusto: {
    id: "gusto",
    name: "Gusto",
    website: "gusto.com",
    category: "hr",
    description: "Modern HR platform combining payroll, benefits, and HR management for small businesses.",

    pricingModel: "hybrid",

    billingCycles: [
      { name: "Monthly" }
    ],

    tiers: [
      {
        name: "Simple",
        price: 40,
        unit: "base",
        billing: "+ $6/person per month",
        description: "For teams of 1-9",
        features: [
          "Full-service payroll",
          "Employee self-service",
          "Health benefits admin",
          "Hiring and onboarding",
          "Workers' comp admin"
        ],
        limits: {
          "Employees": "1-9"
        }
      },
      {
        name: "Plus",
        price: 60,
        unit: "base",
        billing: "+ $9/person per month",
                description: "For growing teams",
        features: [
          "Everything in Simple",
          "Next-day direct deposit",
          "Time tracking",
          "PTO management",
          "Performance reviews",
          "Employee surveys"
        ]
      },
      {
        name: "Premium",
        price: null,
        enterprise: true,
        description: "For scaling companies",
        features: [
          "Everything in Plus",
          "Dedicated support team",
          "Compliance alerts",
          "HR resource center",
          "Tax penalty protection",
          "Advanced hiring tools"
        ]
      },
      {
        name: "Contractor Only",
        price: 35,
        unit: "base",
        billing: "+ $6/person per month",
        description: "For contractor payments only",
        features: [
          "Contractor payments",
          "1099 filings",
          "International contractor payments"
        ]
      }
    ],

    notes: [
      "Simple plan limited to 10+ states",
      "Plus plan required for some features in certain states",
      "Health benefits availability varies by location",
      "Free trial available"
    ],

    lastUpdated: "2026-01-08"
  },

  bamboohr: {
    id: "bamboohr",
    name: "BambooHR",
    website: "bamboohr.com",
    category: "hr",
    description: "All-in-one HR software for small and medium businesses with employee data, onboarding, and performance management.",

    pricingModel: "per-employee",

    tiers: [
      {
        name: "Core",
        price: null,
        description: "Essential HR management",
        features: [
          "Employee records",
          "Standard reporting",
          "PTO tracking",
          "Employee self-service",
          "Workflows",
          "Mobile app"
        ]
      },
      {
        name: "Pro",
        price: null,
                description: "Complete HR platform",
        features: [
          "Everything in Core",
          "Performance management",
          "Employee satisfaction surveys",
          "Advanced reporting",
          "Custom workflows",
          "Applicant tracking"
        ]
      }
    ],

    addOns: [
      {
        name: "Payroll",
        description: "Full-service payroll processing",
        price: null
      },
      {
        name: "Benefits Administration",
        description: "Benefits enrollment and management",
        price: null
      },
      {
        name: "Time Tracking",
        description: "Advanced time and attendance",
        price: null
      }
    ],

    pricingSummary: {
      avgPerSeat: 8,
      perSeatRange: [6, 12],
      quoteCount: 15,
      commonDiscount: 10
    },

    notes: [
      "Contact sales for specific pricing",
      "Per-employee pricing varies by company size",
      "Community-reported: typically $6-12/employee/month",
      "7-day free trial available"
    ],

    enterpriseContact: true,
    lastUpdated: "2026-01-08"
  },

  adp: {
    id: "adp",
    name: "ADP",
    website: "adp.com",
    category: "hr",
    description: "Comprehensive HR, payroll, and workforce management solutions for businesses of all sizes.",

    pricingModel: "custom",

    tiers: [
      {
        name: "RUN Powered by ADP (Small Business)",
        price: null,
        description: "For businesses with 1-49 employees",
        features: [
          "Payroll processing",
          "Tax filing & payments",
          "Employee self-service",
          "New hire reporting",
          "Direct deposit"
        ]
      },
      {
        name: "ADP Workforce Now (Mid-Market)",
        price: null,
                description: "For 50-999 employees",
        features: [
          "Everything in RUN",
          "HR management",
          "Benefits administration",
          "Time & attendance",
          "Talent management",
          "Analytics"
        ]
      },
      {
        name: "ADP Vantage HCM (Enterprise)",
        price: null,
        enterprise: true,
        description: "For 1000+ employees",
        features: [
          "Global payroll",
          "Strategic HCM",
          "Advanced analytics",
          "Compliance services",
          "Custom integrations"
        ]
      }
    ],

    notes: [
      "Pricing varies significantly by company size and features",
      "Contact sales for quote - no public pricing",
      "Wide range of add-on services available",
      "Industry-leading for large enterprises"
    ],

    enterpriseContact: true,
    lastUpdated: "2026-01-08"
  },

  rippling: {
    id: "rippling",
    name: "Rippling",
    website: "rippling.com",
    category: "hr",
    description: "Unified workforce platform combining HR, IT, and Finance management in a single system.",

    pricingModel: "per-employee",

    tiers: [
      {
        name: "Rippling Platform",
        price: 8,
        unit: "employee",
        billing: "per month (base)",
        description: "Core platform access",
        features: [
          "Employee database",
          "Workflow automations",
          "Reporting & analytics",
          "App integrations",
          "Permissions management"
        ]
      },
      {
        name: "Rippling HR Cloud",
        price: null,
        description: "Complete HR management",
        features: [
          "Onboarding & offboarding",
          "PTO management",
          "Document management",
          "Org chart",
          "Learning management"
        ]
      },
      {
        name: "Rippling IT Cloud",
        price: null,
        description: "IT management and security",
        features: [
          "Device management",
          "App management",
          "Password management",
          "Inventory management"
        ]
      },
      {
        name: "Rippling Finance Cloud",
        price: null,
        description: "Financial operations",
        features: [
          "Payroll",
          "Expense management",
          "Corporate cards",
          "Bill pay"
        ]
      }
    ],

    addOns: [
      {
        name: "Payroll",
        description: "Full-service payroll processing",
        price: 8,
        unit: "employee/month"
      },
      {
        name: "Benefits Admin",
        description: "Benefits enrollment and management",
        price: null
      },
      {
        name: "Time & Attendance",
        description: "Time tracking and scheduling",
        price: null
      }
    ],

    notes: [
      "Base platform starts at $8/employee/month",
      "Modular pricing - add what you need",
      "Total cost varies by features selected",
      "Demo required for full pricing"
    ],

    enterpriseContact: true,
    lastUpdated: "2026-01-08"
  },

  // ============================================
  // Security / SIEM
  // ============================================

  splunk: {
    id: "splunk",
    name: "Splunk",
    website: "splunk.com",
    category: "security",
    description: "Enterprise security information and event management (SIEM) platform with advanced analytics and machine learning.",

    pricingModel: "custom",

    tiers: [
      {
        name: "Splunk Cloud Platform",
        price: null,
        description: "Cloud-native SIEM",
        features: [
          "Real-time security monitoring",
          "Threat detection & investigation",
          "Machine learning analytics",
          "Compliance reporting",
          "Cloud-native scalability"
        ]
      },
      {
        name: "Splunk Enterprise",
        price: null,
        description: "Self-managed deployment",
        features: [
          "Full data platform",
          "On-premises or hybrid deployment",
          "Custom apps & integrations",
          "Advanced analytics"
        ]
      },
      {
        name: "Splunk Enterprise Security",
        price: null,
        enterprise: true,
        description: "Premium SIEM add-on",
        features: [
          "Security dashboards",
          "Risk-based alerting",
          "Threat intelligence integration",
          "Investigation workflows",
          "MITRE ATT&CK mapping"
        ]
      }
    ],

    notes: [
      "Multiple pricing models: Workload, Ingest, Entity, Activity-based",
      "Contact sales for specific pricing",
      "Pricing varies significantly by data volume",
      "Free tier available for limited use (Splunk Cloud Trial)"
    ],

    enterpriseContact: true,
    lastUpdated: "2026-01-08"
  },

  datadog: {
    id: "datadog",
    name: "Datadog",
    website: "datadoghq.com",
    category: "security",
    description: "Cloud monitoring and security platform with unified observability, APM, log management, and Cloud SIEM.",

    pricingModel: "usage",

    billingCycles: [
      { name: "Monthly (On-demand)" },
      { name: "Annual", discount: 17 }
    ],

    tiers: [
      {
        name: "Infrastructure Free",
        price: 0,
        description: "Core monitoring features",
        features: [
          "Up to 5 hosts",
          "1-day metric retention",
          "Core collection and visualization"
        ],
        limits: {
          "Hosts": "5",
          "Retention": "1 day"
        }
      },
      {
        name: "Infrastructure Pro",
        price: 15,
        unit: "host",
        billing: "per month (annual)",
        description: "Centralize monitoring",
        features: [
          "1,000+ integrations",
          "Out-of-the-box dashboards",
          "15-month metric retention",
          "5 containers per host allotted",
          "100 custom metrics per host"
        ]
      },
      {
        name: "Infrastructure Enterprise",
        price: 23,
        unit: "host",
        billing: "per month (annual)",
                description: "Advanced features & controls",
        features: [
          "Everything in Pro",
          "Machine learning-based alerts",
          "Live Processes",
          "Governance Console",
          "10 containers per host allotted",
          "200 custom metrics per host"
        ]
      },
      {
        name: "DevSecOps Pro",
        price: 22,
        unit: "host",
        billing: "per month (annual)",
        description: "Infrastructure + Security",
        features: [
          "Everything in Infrastructure Pro",
          "Cloud Security Posture Management",
          "Kubernetes Security Posture Management",
          "Vulnerability Management",
          "Compliance frameworks"
        ]
      },
      {
        name: "DevSecOps Enterprise",
        price: 34,
        unit: "host",
        billing: "per month (annual)",
        enterprise: true,
        description: "Advanced security & threat detection",
        features: [
          "Everything in Infrastructure Enterprise",
          "File integrity monitoring",
          "Workload Protection",
          "10 containers allotted per host"
        ]
      }
    ],

    addOns: [
      {
        name: "Cloud SIEM",
        description: "Security analytics and threat detection",
        price: 5,
        unit: "per million events analyzed/month (annual)"
      },
      {
        name: "Log Management",
        description: "Centralized log collection and analysis",
        price: null
      },
      {
        name: "APM",
        description: "Application performance monitoring",
        price: null
      },
      {
        name: "IoT Device Monitoring",
        description: "Monitor IoT devices",
        price: 5,
        unit: "device/month"
      }
    ],

    notes: [
      "On-demand pricing approximately 17-20% higher than annual",
      "Cloud SIEM: $5/million events (annual) or $7.50 on-demand",
      "Ingestion for Cloud SIEM: $0.10/GB/month",
      "Multi-year/volume discounts available for 500+ hosts"
    ],

    lastUpdated: "2026-01-08"
  },

  "elastic-security": {
    id: "elastic-security",
    name: "Elastic Security",
    website: "elastic.co",
    category: "security",
    description: "Open security platform combining SIEM, endpoint protection, and cloud security built on Elasticsearch.",

    pricingModel: "usage",

    tiers: [
      {
        name: "Security Analytics Essentials",
        price: null,
        description: "Traditional SIEM capabilities",
        features: [
          "Ad hoc analytics & machine learning",
          "Prebuilt detection rules",
          "Triage, investigation, and hunting",
          "Collaboration workflows",
          "Threat intelligence integration"
        ]
      },
      {
        name: "Security Analytics Complete",
        price: null,
                description: "Advanced analytics with AI",
        features: [
          "Everything in Essentials",
          "Entity analytics/UEBA",
          "Threat intelligence management",
          "Bidirectional response framework",
          "Extended security content",
          "Elastic AI Assistant"
        ]
      }
    ],

    addOns: [
      {
        name: "Endpoint Protection Essentials",
        description: "Malware and ransomware prevention",
        price: 0.41,
        unit: "endpoint/month (as low as)"
      },
      {
        name: "Endpoint Protection Complete",
        description: "Full endpoint protection with response",
        price: 0.49,
        unit: "endpoint/month (as low as)"
      },
      {
        name: "Cloud Protection CSPM",
        description: "Cloud security posture management",
        price: 0.65,
        unit: "asset/month (as low as)"
      },
      {
        name: "Cloud Workload Protection",
        description: "Runtime protection for cloud workloads",
        price: 0.41,
        unit: "asset/month (as low as)"
      }
    ],

    pricingSummary: {
      notes: "Usage-based pricing: Essentials $0.09/GB ingest, $0.017/GB retention; Complete $0.11/GB ingest, $0.019/GB retention"
    },

    notes: [
      "Serverless (usage-based) and Hosted (resource-based) options",
      "Self-managed option also available",
      "50GB egress free, then $0.05/GB",
      "Support: Standard (included), Gold (+5%), Platinum (+10%), Enterprise (+15%)"
    ],

    enterpriseContact: true,
    lastUpdated: "2026-01-08"
  },

  okta: {
    id: "okta",
    name: "Okta",
    website: "okta.com",
    category: "security",
    description: "Enterprise identity and access management platform providing SSO, MFA, and lifecycle management.",

    pricingModel: "per-user",

    billingCycles: [
      { name: "Annual" }
    ],

    tiers: [
      {
        name: "Starter",
        price: 6,
        unit: "user",
        billing: "per month (billed annually)",
        description: "Foundation for identity journey",
        features: [
          "Single Sign-On (SSO)",
          "Multi-Factor Authentication",
          "Universal Directory",
          "5 Workflows"
        ]
      },
      {
        name: "Core Essentials",
        price: 14,
        unit: "user",
        billing: "per month (billed annually)",
        description: "Core capabilities without advanced security",
        features: [
          "Everything in Starter",
          "Adaptive MFA",
          "Lifecycle Management",
          "5 Workflows"
        ]
      },
      {
        name: "Essentials",
        price: 17,
        unit: "user",
        billing: "per month (billed annually)",
                description: "Advanced automation and security",
        features: [
          "Everything in Core Essentials",
          "Privileged Access (2 admins)",
          "Access Governance",
          "50 Workflows"
        ]
      },
      {
        name: "Professional",
        price: null,
        enterprise: true,
        description: "Device access and AI intelligence",
        features: [
          "Everything in Essentials",
          "Device Access",
          "Identity Security Posture Management",
          "Identity Threat Protection with Okta AI",
          "Sandbox",
          "Unlimited Workflows"
        ]
      },
      {
        name: "Enterprise",
        price: null,
        enterprise: true,
        description: "End-to-end security with APIs",
        features: [
          "Everything in Professional",
          "API Access Management",
          "Access Gateway",
          "50 ISPM integrations",
          "Machine-to-Machine Tokens"
        ]
      }
    ],

    addOns: [
      {
        name: "Adaptive MFA",
        description: "Risk-based authentication",
        price: null
      },
      {
        name: "Device Access",
        description: "Passwordless desktop login",
        price: null
      },
      {
        name: "API Access Management",
        description: "Secure API endpoints",
        price: null
      },
      {
        name: "Identity Threat Protection",
        description: "AI-powered threat detection",
        price: null
      }
    ],

    notes: [
      "$1,500 annual contract minimum",
      "Professional and Enterprise require sales contact",
      "30-day free trial available",
      "Most products available as standalone add-ons"
    ],

    enterpriseContact: true,
    lastUpdated: "2026-01-08"
  }
};

// Export function to get software by category
export const getSoftwareByCategory = (categoryId) => {
  return Object.values(pricingData).filter(sw => sw.category === categoryId);
};

// Export function to get all software
export const getAllSoftware = () => Object.values(pricingData);

// Export function to get software by ID
export const getSoftwareById = (id) => pricingData[id];
